<template>
  <div
    v-show="!noRewards"
    class="col-span-full grid [@media(min-width:550px)]:grid-cols-2 lg:!grid-cols-6 gap-4 border rounded-md p-4"
    :class="{
      [`cards-${totalCardCount}`]: true,
      'border-yellow-500 bg-yellow-600/20': subscribeLink,
      'border-nami-comi-blue bg-nami-comi-blue/20': !subscribeLink,
    }"
  >
    <OrganizationRewardSectionHeader
      :subscribe-link="subscribeLink"
      class="col-span-full text-center my-4"
    >
      <template v-if="tier">
        By subscribing to
        <NuxtLink
          class="text-nami-comi-blue canhover:hover:underline"
          :href="
            href.org.slug.subscriptions(org.attributes.slug) +
            `?highlightTier=${tier.id}&highlightText=Selected Tier`
          "
          >{{ extractFromLocale(locale, tier.attributes.name) }}
        </NuxtLink>
      </template>
      <template v-else> By following this creator </template>
      <!--    <span class="text-base px-2 py-1 bg-neutral-800 rounded-full align-middle">{{ tier.attributes.minAmounts.USD! / 100}}$/mo</span>-->
      <template #subtext v-if="tier">
        Pledge ${{ tier.attributes.minAmounts.USD! / 100 }}/mo to receive these
        rewards.
      </template>
      <template #subtext v-else>
        Following is free and keeps you notified of any updates.
      </template>
    </OrganizationRewardSectionHeader>

    <!--  <Card v-if="perks.length && !noRewards" class="flex flex-col items-center">-->
    <!--    <h3 class="font-bold text-center text-lg">-->
    <!--      For {{ tier.attributes.minAmounts.USD! / 100 }}$/mo-->
    <!--    </h3>-->
    <!--    <ul class="flex justify-center flex-col flex-grow">-->
    <!--      <li-->
    <!--        v-for="feature in perks"-->
    <!--        class="text-sm sm:text-base text-center list-none"-->
    <!--      >-->
    <!--        {{ feature }}-->
    <!--      </li>-->
    <!--    </ul>-->
    <!--    <NamiButton button-type="primary" text :href="subscribeLink" block>-->
    <!--      Subscribe now-->
    <!--    </NamiButton>-->
    <!--  </Card>-->

    <!--  posts, chapters, frame, emotes, stickers-->
    <OrganizationRewardCardPosts v-if="posts" :org :subscribeLink />
    <OrganizationRewardCardChapters :titles :subscribeLink />
    <OrganizationRewardCardFrames :frames :subscribeLink />
    <OrganizationRewardCardEmotes :emotes :subscribeLink />
    <OrganizationRewardCardStickers :stickers :subscribeLink />
  </div>
</template>

<script lang="ts" setup>
import {
  type Collection,
  type EmoteEntity,
  type FrameEntity,
  isExpanded,
  isTier,
  type OrganizationEntity,
  type TierEntity,
  type TitleEntity,
} from "~/src/api";

const props = defineProps<{
  org: OrganizationEntity;
  tier?: TierEntity;
  emotes?: Collection<EmoteEntity> | null;
  stickers?: Collection<EmoteEntity> | null;
  frames?: Collection<FrameEntity> | null;
  titles: TitleEntity[] | null;
  exclusive?: boolean;
}>();

const href = useHref();
const route = useRoute();
const { locale } = useNuxtApp().$i18n.global;

const subscribeLink = computed(() => {
  if (props.tier)
    return (
      href.value.org.slug.subscriptions(route.params.slug as string) +
      `?highlightTier=${props.tier.id}&highlightText=Your Selection`
    );
  return undefined;
});

const isForThisTier = (input: EmoteEntity | FrameEntity) => {
  if (!props.tier) return input.attributes.requirement === "follow";

  if (input.attributes.requirement !== "subscription") return false;
  const foundTier = input.relationships.find(isTier);

  if (!foundTier) return false;
  if (foundTier.id === props.tier.id) return true;
  if (!isExpanded(foundTier)) return false;

  if (props.exclusive) return false;
  // dont return items below this tier if we're on overview
  return (
    foundTier.attributes.minAmounts.USD! <=
    props.tier.attributes.minAmounts.USD!
  );
};

// const perks = computed(() =>
//   extractFromLocale(locale.value, props.tier.attributes.description)
//     .split("\n")
//     .filter((x) => !!x),
// );

const emotes = computed(() => props.emotes?.data.filter(isForThisTier));
const stickers = computed(() => props.stickers?.data.filter(isForThisTier));
const frames = computed(() => props.frames?.data.filter(isForThisTier));
const titles = computed(() => (props.tier ? props.titles : []));
const posts = computed(() => !!props.tier);

const totalCardCount = computed(() => {
  // show posts by default always, will be changed later
  let count = posts.value ? 1 : 0;
  if (emotes.value?.length) count++;
  if (stickers.value?.length) count++;
  if (frames.value?.length) count++;
  if (titles.value?.length) count++;
  return count;
});

const noRewards = computed(() => totalCardCount.value === 0);
</script>

<style lang="postcss" scoped>
.cards-1 {
  & > :nth-child(2) {
    @apply [@media(min-width:550px)]:col-span-2 lg:!col-start-3;
  }
}

.cards-2 {
  & > :nth-child(2) {
    @apply lg:col-span-3;
  }

  & > :nth-child(3) {
    @apply lg:col-span-3;
  }
}

.cards-3 {
  & > :nth-child(2) {
    @apply lg:col-span-2;
  }

  & > :nth-child(3) {
    @apply lg:col-span-2;
  }

  & > :nth-child(4) {
    @apply lg:col-span-2;
  }
}

.cards-4 {
  & > :nth-child(2) {
    @apply lg:col-span-3;
  }

  & > :nth-child(3) {
    @apply lg:col-span-3;
  }

  & > :nth-child(4) {
    @apply lg:col-span-3;
  }

  & > :nth-child(5) {
    @apply lg:col-span-3;
  }
}

.cards-5 {
  & > :nth-child(2) {
    @apply lg:col-span-3;
  }

  & > :nth-child(3) {
    @apply lg:col-span-3;
  }

  & > :nth-child(4) {
    @apply lg:col-span-2;
  }

  & > :nth-child(5) {
    @apply lg:col-span-2;
  }

  & > :nth-child(6) {
    @apply lg:col-span-2;
  }
}
</style>
